import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getBalance, CreditBalance } from '../services/creditService';
import { useAuth } from './AuthContext';

interface CreditsContextType {
  balance: number;
  currency: string;
  loading: boolean;
  error: string | null;
  refreshBalance: () => Promise<void>;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

export function CreditsProvider({ children }: { children: ReactNode }) {
  const { userProfile } = useAuth();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshBalance = useCallback(async () => {
    if (!userProfile?.organisation_id) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const balanceData = await getBalance();
      setBalance(balanceData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load balance');
      console.error('Error loading balance:', err);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.organisation_id]);

  useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  useEffect(() => {
    const handleCreditsUpdate = () => {
      void refreshBalance();
    };

    window.addEventListener('credits-updated', handleCreditsUpdate);
    return () => window.removeEventListener('credits-updated', handleCreditsUpdate);
  }, [refreshBalance]);

  return (
    <CreditsContext.Provider
      value={{
        balance: balance?.balance ?? 0,
        currency: balance?.currency ?? 'CREDITS',
        loading,
        error,
        refreshBalance,
      }}
    >
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
}
